import localforage from 'localforage';

// Configure localforage for offline storage
localforage.config({
  name: 'SnackStation',
  storeName: 'offline_data',
  description: 'Offline data storage for SnackStation'
});

// Separate stores for different data types
export const offlineStores = {
  products: localforage.createInstance({ name: 'products' }),
  compartments: localforage.createInstance({ name: 'compartments' }),
  inventory: localforage.createInstance({ name: 'inventory' }),
  refills: localforage.createInstance({ name: 'refills' }),
  transactions: localforage.createInstance({ name: 'transactions' }),
  syncQueue: localforage.createInstance({ name: 'syncQueue' }),
  conflicts: localforage.createInstance({ name: 'conflicts' }),
  meta: localforage.createInstance({ name: 'meta' })
};

// Generate unique ID for offline entries
export const generateOfflineId = () => `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Check if online
export const isOnline = () => navigator.onLine;

// Add item to sync queue
export const addToSyncQueue = async (action) => {
  const queue = await getSyncQueue();
  const entry = {
    id: generateOfflineId(),
    ...action,
    timestamp: Date.now(),
    retries: 0
  };
  queue.push(entry);
  await offlineStores.syncQueue.setItem('queue', queue);
  return entry;
};

// Get sync queue
export const getSyncQueue = async () => {
  const queue = await offlineStores.syncQueue.getItem('queue');
  return queue || [];
};

// Remove from sync queue
export const removeFromSyncQueue = async (id) => {
  const queue = await getSyncQueue();
  const filtered = queue.filter(item => item.id !== id);
  await offlineStores.syncQueue.setItem('queue', filtered);
};

// Add conflict for manual resolution
export const addConflict = async (conflict) => {
  const conflicts = await getConflicts();
  conflicts.push({
    id: generateOfflineId(),
    ...conflict,
    timestamp: Date.now(),
    resolved: false
  });
  await offlineStores.conflicts.setItem('conflicts', conflicts);
};

// Get conflicts
export const getConflicts = async () => {
  const conflicts = await offlineStores.conflicts.getItem('conflicts');
  return conflicts || [];
};

// Resolve conflict
export const resolveConflict = async (id, resolution) => {
  const conflicts = await getConflicts();
  const updated = conflicts.map(c => 
    c.id === id ? { ...c, resolved: true, resolution } : c
  );
  await offlineStores.conflicts.setItem('conflicts', updated);
};

// Clear resolved conflicts
export const clearResolvedConflicts = async () => {
  const conflicts = await getConflicts();
  const unresolved = conflicts.filter(c => !c.resolved);
  await offlineStores.conflicts.setItem('conflicts', unresolved);
};

// Cache data locally
export const cacheData = async (type, data) => {
  const store = offlineStores[type];
  if (store) {
    await store.setItem('data', data);
    await offlineStores.meta.setItem(`${type}_lastSync`, Date.now());
  }
};

// Get cached data
export const getCachedData = async (type) => {
  const store = offlineStores[type];
  if (store) {
    return await store.getItem('data');
  }
  return null;
};

// Get last sync time
export const getLastSync = async (type) => {
  return await offlineStores.meta.getItem(`${type}_lastSync`);
};

// Clear all offline data
export const clearOfflineData = async () => {
  for (const store of Object.values(offlineStores)) {
    await store.clear();
  }
};

export default offlineStores;
